import {
  MarketPowerItem,
  RetailPowerItem,
} from "@/domain/interfaces/general.interface.ts";
import { IActivityDetector } from "@/hooks/useActivityDetector.ts";
import { ICryptoDrift } from "@/hooks/useCryptoDrift.ts";
import {
  getAggregatedConstructorTopic,
  socketConnection,
} from "@/lib/natsConnection.ts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

export type IValuesByTime = {
  "15m": number;
  "30m": number;
  "1h": number;
  "4h": number;
  "1d": number;
  "1w": number;
  "1M": number;
};

// {"timestamp":1732693242179,"symbol":"DOTUSDT","priceChg":{"15m":-0.036,"1M":96.556,"1d":2.303,"1h":0.22,"1w":37.995,"30m":0.415,"4h":1.922},"openInterestChg":{"15m":0.302,"1M":1.326,"1d":1.421,"1h":0.169,"1w":1.932,"30m":0.34,"4h":1.823},"volumeChg":{"15m":5.46,"1M":195.896,"1d":8.063,"1h":-25.042,"1w":101.904,"30m":17.789,"4h":-24.233},"buySellRatioChg":{"15m":50.377,"1M":48.425,"1d":48.528,"1h":52.916,"1w":48.766,"30m":52.736,"4h":51.702},"volumeQuote":{"15m":3268812,"1M":10339706497,"1d":527286016,"1h":11726631,"1w":5573277132,"30m":6359978,"4h":48942536},"adtv":{"15m":91.71,"1M":242.22,"1d":94.56,"1h":87.57,"1w":153.89,"30m":109.14,"4h":100.86},"funding":0.00024883,"openInterest":13785701.8,"manipulationMonitor":{"corr":0.998512,"corrV":0.976352},"powerCvd":{"value":1.578,"isShort":false,"isLong":false},"spotFutureSpread":{"sma":-0.086,"value":-0.061}}
export interface IAggregatedConstructor {
  timestamp: number;
  symbol: string;
  priceChg: IValuesByTime;
  openInterestChg: IValuesByTime;
  volumeChg: IValuesByTime;
  buySellRatioChg: IValuesByTime;
  volumeQuote: IValuesByTime;
  adtv: IValuesByTime;
  funding: number;
  openInterest: number;
  manipulationMonitor: {
    corr: number;
    corrV: number;
  };
  powerCvd: {
    value: number;
    isShort: boolean;
    isLong: boolean;
  };
  spotFutureSpread: {
    sma: number;
    value: number;
  };
  cryptoDrift: ICryptoDrift;
  activityDetector: IActivityDetector;
  marketPower: MarketPowerItem;
  retailPower: RetailPowerItem;
}

export type IAggregatedData = Record<string, IAggregatedConstructor>;

const key = ["aggregatedData"];
const BATCH_UPDATE_INTERVAL = 1000; // Increased to 1 second
const THROTTLE_INTERVAL = 2000; // Add throttle interval

export function useAggregatedConstructor(symbols: string[]) {
  const unsubscribeRef = useRef<Record<string, () => void>>({});
  const queryClient = useQueryClient();
  const oldSymbols = useRef<string[]>([]);
  const batchedUpdatesRef = useRef<Record<string, IAggregatedConstructor>>({});
  const batchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastUpdateTimeRef = useRef<number>(Date.now());
  const rafIdRef = useRef<number | null>(null);

  const processBatchedUpdates = useCallback(() => {
    if (Object.keys(batchedUpdatesRef.current).length === 0) {
      return;
    }

    const now = Date.now();
    if (now - lastUpdateTimeRef.current < THROTTLE_INTERVAL) {
      // Schedule next update using RAF
      rafIdRef.current = requestAnimationFrame(() => {
        batchTimeoutRef.current = setTimeout(() => {
          processBatchedUpdates();
          batchTimeoutRef.current = null;
        }, BATCH_UPDATE_INTERVAL);
      });
      return;
    }

    queryClient.setQueryData<IAggregatedData>(
      key,
      (currentData: Record<string, IAggregatedConstructor> = {}) => {
        const updates = { ...batchedUpdatesRef.current };
        batchedUpdatesRef.current = {};

        // Only update changed values
        const newData = { ...currentData };
        let hasChanges = false;

        Object.entries(updates).forEach(([symbol, newValue]) => {
          const oldValue = currentData[symbol];
          if (
            !oldValue ||
            JSON.stringify(oldValue) !== JSON.stringify(newValue)
          ) {
            newData[symbol] = newValue;
            hasChanges = true;
          }
        });

        lastUpdateTimeRef.current = now;
        return hasChanges ? newData : currentData;
      },
    );
  }, [queryClient]);

  const scheduleUpdate = useCallback(() => {
    if (batchTimeoutRef.current) {
      return;
    }

    rafIdRef.current = requestAnimationFrame(() => {
      batchTimeoutRef.current = setTimeout(() => {
        processBatchedUpdates();
        batchTimeoutRef.current = null;
      }, BATCH_UPDATE_INTERVAL);
    });
  }, [processBatchedUpdates]);

  useEffect(() => {
    const newSymbols = symbols.filter((s) => !oldSymbols.current.includes(s));
    const removedSymbols = oldSymbols.current.filter(
      (s) => !symbols.includes(s),
    );

    // Subscribe to new symbols
    newSymbols.forEach((symbol) => {
      const topic = getAggregatedConstructorTopic(symbol);
      socketConnection
        .subscribe(topic, (newData: IAggregatedConstructor) => {
          batchedUpdatesRef.current[newData.symbol] = newData;
          scheduleUpdate();
        })
        .then((unsub) => {
          unsubscribeRef.current[symbol] = unsub;
        });
    });

    // Cleanup removed symbols
    removedSymbols.forEach((symbol) => {
      if (unsubscribeRef.current[symbol]) {
        unsubscribeRef.current[symbol]();
        delete unsubscribeRef.current[symbol];
        delete batchedUpdatesRef.current[symbol];

        queryClient.setQueryData<IAggregatedData>(
          key,
          (currentData: Record<string, IAggregatedConstructor> = {}) => {
            const { [symbol]: removed, ...rest } = currentData;
            return rest;
          },
        );
      }
    });

    oldSymbols.current = symbols;

    return () => {
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
      }
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [queryClient, symbols, scheduleUpdate]);

  useEffect(() => {
    const currentUnsubscribe = { ...unsubscribeRef.current };
    return () => {
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
      }
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      Object.values(currentUnsubscribe).forEach((unsub) => unsub());
    };
  }, []);

  return useQuery<IAggregatedData>({
    queryKey: key,
    enabled: true,
    staleTime: Infinity, // Prevent automatic refetching
    gcTime: Infinity, // Prevent garbage collection
  });
}
